import React, { useState } from "react";
import RiderCard from "../../common/components/RiderCard";
import {
  getRiderProductBounds,
  RIDER_ID_ADD_PLUS,
  RIDER_ID_CI,
} from "../../common/utils/riderCoverRules";
import {
  ADD_PLUS_CI_EXCLUSIVE_TITLE,
  RIDER_FALLBACK_YEARLY_PREMIUM,
} from "./constants/riderConstants";
import { getFrequencyDivisor } from "./utils/premiumFrequency";
import { Modal } from "../../common/components";
import { descriptionMap,knowMore } from "../../constants";

/**
 * Builds the "Starts @ ₹X" fallback label for a rider card. The configured
 * amount is yearly; for other frequencies we divide by the installments-per-year
 * (Monthly /12, Quarterly /4, Half-Yearly /2, Yearly /1).
 */
const getRiderFallbackPremiumLabel = (riderId, premiumFrequency) => {
  const yearly = RIDER_FALLBACK_YEARLY_PREMIUM[riderId];
  if (yearly == null) return "Starts @ ₹22";
  const divisor = getFrequencyDivisor(premiumFrequency);
  const amount = yearly / divisor;
  const formatted = amount.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `Starts @ ₹${formatted}`;
};

function renderRiderRow(
  riders,
  {
    selectedRiders,
    enhancedCoverageEnabled,
    effectiveCoverAmount,
    riderCovers,
    riderCoverDrafts,
    riderCoverErrors,
    premiumFrequency,
    getRiderPremium,
    markUserEditedQuote,
    toggleRider,
    onCoverInputChange,
    onCoverInputBlur,
    handleKnowMore,
  },
) {
  return riders.map((rider) => {
    const bounds = getRiderProductBounds(rider.id);
    const toggleBlockedEcbo =
      rider.id === RIDER_ID_ADD_PLUS && !!enhancedCoverageEnabled;
    const toggleBlockedCiPair =
      (rider.id === RIDER_ID_CI && selectedRiders.has(RIDER_ID_ADD_PLUS)) ||
      (rider.id === RIDER_ID_ADD_PLUS && selectedRiders.has(RIDER_ID_CI));
    const toggleDisabled = toggleBlockedEcbo || toggleBlockedCiPair;
    const toggleDisabledTitle = toggleBlockedEcbo
      ? "Turn off Enhanced Coverage Benefit to add this rider."
      : toggleBlockedCiPair
        ? ADD_PLUS_CI_EXCLUSIVE_TITLE
        : "";
    return (
      <RiderCard
        key={rider.id}
        rider={rider}
        description={rider.description}
        onKnowMore={handleKnowMore}
        isSelected={selectedRiders.has(rider.id)}
        riderPremium={getRiderPremium(rider.id)}
        premiumFrequency={premiumFrequency}
        defaultPremiumLabel={getRiderFallbackPremiumLabel(
          rider.id,
          premiumFrequency,
        )}
        coverEntry={rider.id === RIDER_ID_ADD_PLUS ? "readonly" : "text"}
        coverValue={
          rider.id === RIDER_ID_ADD_PLUS
            ? effectiveCoverAmount
            : (riderCovers[rider.id] ?? rider.minCover)
        }
        coverInputValue={
          rider.id === RIDER_ID_ADD_PLUS
            ? ""
            : (riderCoverDrafts[rider.id] ??
              (riderCovers[rider.id] != null
                ? String(riderCovers[rider.id])
                : ""))
        }
        coverError={riderCoverErrors[rider.id] ?? ""}
        coverPlaceholder={`Min ₹${bounds.min.toLocaleString("en-IN")} · max ₹${bounds.max.toLocaleString("en-IN")} (≤ life cover, ₹1,000 steps)`}
        coverOptions={[]}
        onToggle={(...args) => {
          markUserEditedQuote();
          toggleRider(...args);
        }}
        onCoverChange={() => {}}
        onCoverInputChange={onCoverInputChange}
        onCoverInputBlur={onCoverInputBlur}
        toggleDisabled={toggleDisabled}
        toggleDisabledTitle={toggleDisabledTitle}
      />
    );
  });
}

/**
 * Add-ons (riders) block for Quote: two rows of rider cards with text/readonly cover entry.
 */
export default function QuoteRidersSection({
  ridersData,
  selectedRiders,
  enhancedCoverageEnabled,
  effectiveCoverAmount,
  riderCovers,
  riderCoverDrafts,
  riderCoverErrors,
  premiumFrequency,
  getRiderPremium,
  markUserEditedQuote,
  toggleRider,
  onCoverInputChange,
  onCoverInputBlur,
}) {
  const [selectedRider, setSelectedRider] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleKnowMore = (rider) => {
    setSelectedRider(rider);
    setShowModal(true);
  };
  const cardProps = {
    selectedRiders,
    enhancedCoverageEnabled,
    effectiveCoverAmount,
    riderCovers,
    riderCoverDrafts,
    riderCoverErrors,
    premiumFrequency,
    getRiderPremium,
    markUserEditedQuote,
    toggleRider,
    onCoverInputChange,
    onCoverInputBlur,
    handleKnowMore,
  };
  const enrichedRidersData = ridersData.map((rider) => ({
    ...rider,
    description: descriptionMap[rider.id] || "",
  }));

  const row1 = enrichedRidersData.slice(0, 2);
  const row2 = enrichedRidersData.slice(2, 4);

  return (
    <div className="add-ons-section card-plan-list">
      <h6 className="section-title m-0">Add-ons</h6>
      <div className="add-ons-group-a">{renderRiderRow(row1, cardProps)}</div>
      <div className="add-ons-group-a">{renderRiderRow(row2, cardProps)}</div>
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={selectedRider?.name || "Rider Details"}
        type="success"
      >
        <p>
          {selectedRider
            ? knowMore[selectedRider.id] || selectedRider.description
            : "No details available"}
        </p>
      </Modal>
    </div>
  );
}
